import {useEffect, useRef} from 'react'
import {FilterUrlParams} from 'modules/search/types'
import {toArray} from 'utils/object'

import {
  RouterContextProps,
  useRouterProvider
} from '@vio/core/src/_web/router/context/RouterContext'
import {
  safelyGetStorageItem,
  safelySetStorageItem
} from '@vio/core/src/_web/utils/persistence'

import {
  getAvailableRecentlyUsedFilters,
  GetAvailableRecentlyUsedFiltersProps,
  getCleanedFilters,
  getValidMappedAllRecentlyUsedFilters
} from './utils'

/**
 * LocalStorage key for storing recently used filters.
 *
 * Storage format: Array of filter strings in "filterType:filterValue" format
 * Example: ["facilities:1", "price:100-200", "reviewScore:9"]
 *
 * Updated in real-time as filters are applied during the current session.
 */
const RECENTLY_USED_FILTERS_STORAGE_KEY = 'filters_recently_used'

/**
 * URL parameters that represent user-applied filters.
 * These are tracked separately from search identifiers to determine
 * which filters users prefer across sessions.
 */
const FILTER_KEYS: (keyof FilterUrlParams)[] = [
  'chainIds',
  'facilities',
  'freeCancellation',
  'reviewScore',
  'hotelName',
  'noHostels',
  'priceMax',
  'priceMin',
  'propertyTypes',
  'starRatings',
  'themes',
  'sortField',
  'sortOrder',
  'amenities',
  'offerFilters',
  'roomFilters',
  'payLater',
  'brandIds',
  'neighborhoodIds',
  'exclusive',
  'dealFreeze' // Added as a part of c747927f-deal-freeze-srp
]

/**
 * Extracts filters parameters from URL query parameters.
 *
 * @param getQueryParams - Function to retrieve current URL query parameters
 * @returns Object containing filters
 */
export const getParamsDetection = (
  getQueryParams: RouterContextProps['getQueryParams']
): FilterUrlParams => {
  const params = getQueryParams()
  const filters = new Map<keyof FilterUrlParams, unknown>()

  Object.keys(params).forEach(key => {
    if (FILTER_KEYS.includes(key as keyof FilterUrlParams)) {
      filters.set(key as keyof FilterUrlParams, params[key])
    }
  })

  return Object.fromEntries(filters)
}

/**
 * Hook to track and manage recently used filters.
 *
 * This hook monitors URL filter parameters and automatically tracks which filters
 * users apply during their browsing session. The tracked filters are stored in
 * localStorage and can be retrieved later to suggest relevant filters to the user.
 *
 * ## Key Features:
 * - Automatically tracks newly applied filters in real-time
 * - Persists filter data to localStorage
 * - Cleans duplicate filters (keeps only latest reviewScore/price)
 * - Provides validated filters based on current search context
 * - Filters out already selected and unavailable options
 *
 * ## How It Works:
 * 1. User applies filter (e.g., "Pool", "4+ stars", "$100-$200")
 * 2. Hook detects the change and stores it as "facilities:1", "starRatings:4", "price:100-200"
 * 3. Filters are persisted to localStorage immediately
 * 4. When user searches again, `getRecentlyUsedFilters()` returns relevant suggestions
 * 5. Only filters that are available in current search results are shown
 *
 * ## Storage Format:
 * localStorage stores a flat array of filter strings:
 * ```json
 * ["facilities:1", "facilities:7", "starRatings:4", "price:100-200", "reviewScore:9"]
 * ```
 *
 * ## Public Methods:
 * - `getAllRecentlyUsedFilters()`: Returns raw array of stored filter strings
 * - `getRecentlyUsedFilters({...})`: Returns validated, filtered suggestions for UI
 *
 * @example
 * ```tsx
 * const { getRecentlyUsedFilters } = useRecentlyUsedFilters()
 *
 * const suggestions = getRecentlyUsedFilters({
 *   appliedFilters: currentFilters,
 *   facets: searchResults.facets,
 *   neighborhoods: availableNeighborhoods,
 *   propertyBrands: availableBrands,
 *   limit: 5
 * })
 * ```
 *
 */
export const useRecentlyUsedFilters = () => {
  const {getQueryParams} = useRouterProvider()

  const filters = getParamsDetection(getQueryParams)

  /**
   * Ref to store the previous filter state for comparison.
   *
   * Used to detect which filters are newly applied by comparing
   * the current URL filters with the previous ones. This allows
   * tracking only additions, not removals.
   */
  const currentFiltersRef = useRef<FilterUrlParams>()

  /**
   * Ref to maintain the accumulated list of recently used filters.
   *
   * Stores the current list of tracked filters across renders without
   * triggering re-renders. This ref is synchronized with localStorage
   * and is updated whenever new filters are detected.
   */
  const currentSessionRef = useRef<string[]>()

  /**
   * Effect: Initialize or restore filters from localStorage on mount.
   *
   * This effect runs once when the hook is first initialized. It:
   * 1. Checks if there are existing filters in localStorage
   * 2. If found, restores them to the ref
   * 3. If not found, initializes storage with current URL filters
   *
   * The effect is designed to be idempotent - it only initializes
   * if the ref is not already set, preventing duplicate storage writes.
   */
  useEffect(() => {
    const handleInitializeSession = () => {
      // Skip if running on server (SSR)
      if (typeof window === 'undefined') return

      const storedRecentlyUsedData = safelyGetStorageItem(
        localStorage,
        RECENTLY_USED_FILTERS_STORAGE_KEY
      )
      const storedFilters = storedRecentlyUsedData
        ? JSON.parse(storedRecentlyUsedData)
        : []

      // Restore from localStorage if data exists and ref is empty
      if (
        storedRecentlyUsedData &&
        !currentSessionRef.current &&
        Array.isArray(storedFilters)
      ) {
        currentSessionRef.current = JSON.parse(storedRecentlyUsedData)
      } else if (!currentSessionRef.current) {
        // Initialize with current URL filters if no stored data
        const initialSession = getAppliedFilters(filters)

        safelySetStorageItem(
          localStorage,
          RECENTLY_USED_FILTERS_STORAGE_KEY,
          JSON.stringify(initialSession)
        )

        currentSessionRef.current = initialSession
      }
    }

    handleInitializeSession()
  }, [filters])

  /**
   * Effect: Track newly applied filters and persist to localStorage.
   *
   * This effect runs whenever URL filter parameters change. It:
   * 1. Compares current filters with previous filters (from ref)
   * 2. Identifies which filters are newly added (not removed)
   * 3. Merges new filters with existing tracked filters
   * 4. Cleans duplicates (keeps only latest reviewScore/price)
   * 5. Persists the updated list to localStorage
   *
   * The effect only tracks filter additions, not removals, to build a
   * comprehensive history of what filters the user has tried. This allows
   * us to suggest relevant filters even if the user removed them temporarily.
   *
   * Example flow:
   * - User applies "Pool" filter → saved as "facilities:1"
   * - User applies "4-star" filter → saved as "starRatings:4"
   * - User changes price to $100-$200 → saved as "price:100-200"
   * - User changes price to $150-$250 → old "price:100-200" is replaced
   */
  useEffect(() => {
    const saveSessionFilters = () => {
      // Skip if running on server (SSR)
      if (typeof window === 'undefined') return

      // On first run, just save current filters as baseline
      if (!currentFiltersRef.current) {
        currentFiltersRef.current = filters
        return
      }

      // Find filters that were just added (not present in previous state)
      const newAppliedFilters = getRecentlyAppliedFilters(
        filters,
        currentFiltersRef.current
      )

      // Only update storage if there are actual new filters
      if (newAppliedFilters.length > 0) {
        // Merge new filters with existing ones (Set handles deduplication)
        const updatedFilters = new Set([
          ...newAppliedFilters,
          ...currentSessionRef.current
        ])

        // Clean up duplicate reviewScore/price filters
        const updatedSession = getCleanedFilters([...updatedFilters])

        // Update both ref and localStorage
        currentSessionRef.current = updatedSession
        currentFiltersRef.current = filters

        const persistedSession = updatedSession.filter(key => {
          return !(key.startsWith('sortOrder') || key.startsWith('sortField'))
        })

        safelySetStorageItem(
          localStorage,
          RECENTLY_USED_FILTERS_STORAGE_KEY,
          JSON.stringify(persistedSession)
        )
      }
    }

    saveSessionFilters()
  }, [filters])

  /**
   * Converts filter URL parameters into standardized filter strings.
   *
   * This function transforms the filter object from the URL into a flat array
   * of strings that can be easily stored, compared, and deduplicated.
   *
   * ## Serialization Format:
   * Each filter becomes a string in the format "filterType:filterValue"
   *
   * ## Examples:
   * ```typescript
   * Input: { facilities: ['1', '7'], starRatings: ['4'], priceMin: 100, priceMax: 200 }
   * Output: ['facilities:1', 'facilities:7', 'starRatings:4', 'price:100-200']
   * ```
   *
   * ## Special Handling:
   * - **Array filters** (facilities, amenities, etc.): Each value becomes a separate entry
   *   - `{ facilities: ['1', '7'] }` → `['facilities:1', 'facilities:7']`
   *
   * - **Price filters** (priceMin/priceMax): Combined into single "price:min-max" format
   *   - `{ priceMin: 100, priceMax: 200 }` → `['price:100-200']`
   *
   * - **Empty/falsy values**: Automatically filtered out
   *   - `{ facilities: ['1', '', null] }` → `['facilities:1']`
   *
   * @param filters - Filter URL parameters object from router
   * @returns Array of filter strings in the format "key:value"
   */
  const getAppliedFilters = (filters: FilterUrlParams): string[] => {
    const appliedFiltersSet: Set<string> = new Set()

    const filterKeys = Object.keys(filters) as (keyof FilterUrlParams)[]

    filterKeys.forEach(key => {
      const filterValue = filters[key]

      // Handle array-type filters: expand each value into a separate entry
      // Example: facilities: ['1', '2'] → 'facilities:1', 'facilities:2'
      if (Array.isArray(filterValue) && filterValue.length > 0) {
        filterValue
          .filter(value => Boolean(value)) // Remove falsy values
          .forEach(value => appliedFiltersSet.add(`${key}:${value}`))
      } else if (
        !Array.isArray(filterValue) &&
        filterValue &&
        key !== 'priceMin' &&
        key !== 'priceMax'
      ) {
        // Handle single-value filters (reviewScore, hotelName, etc.)
        appliedFiltersSet.add(`${key}:${filterValue}`)
      }
    })

    // Special handling: Combine priceMin and priceMax into single entry
    if (filters.priceMin || filters.priceMax) {
      appliedFiltersSet.add(`price:${filters.priceMin}-${filters.priceMax}`)
    }

    return [...appliedFiltersSet]
  }

  /**
   * Identifies filters that have been newly applied by comparing current state with previous state.
   *
   * This function performs a differential comparison between the current URL filters
   * and the previous URL filters to determine which filters were just added by the user.
   *
   * ## Key Behavior:
   * - **Only tracks additions, not removals** - Builds a history of what filters the user has tried
   * - **Deduplicates automatically** - Uses a Set internally
   * - **Handles different filter types** - Array filters, single-value filters, and price ranges
   *
   * ## Comparison Logic:
   *
   * ### Array-type filters (facilities, amenities, themes, etc.):
   * Only new values not present in previous state are tracked
   * ```typescript
   * Previous: { facilities: ['1'] }
   * Current:  { facilities: ['1', '2', '3'] }
   * Result:   ['facilities:2', 'facilities:3']  // Only the new ones
   * ```
   *
   * ### Single-value filters (reviewScore, hotelName, etc.):
   * Any change in value is considered a new application
   * ```typescript
   * Previous: { reviewScore: '7' }
   * Current:  { reviewScore: '9' }
   * Result:   ['reviewScore:9']
   * ```
   *
   * ### Price filters (priceMin/priceMax):
   * Always tracked when present (changes will be cleaned by getCleanedFilters)
   * ```typescript
   * Previous: { priceMin: 100, priceMax: 200 }
   * Current:  { priceMin: 150, priceMax: 250 }
   * Result:   ['price:150-250']
   * ```
   *
   * @param currentFilters - Current state of all filter parameters from URL
   * @param previouslyAppliedFilters - Previous state of filters to compare against
   * @returns Array of newly applied filter strings in the format "key:value"
   */
  const getRecentlyAppliedFilters = (
    currentFilters: FilterUrlParams,
    previouslyAppliedFilters: FilterUrlParams
  ): string[] => {
    // Using a Set internally to automatically deduplicate any duplicate filter strings
    const newlyAppliedFiltersSet: Set<string> = new Set()

    const filterKeys = Object.keys(currentFilters) as (keyof FilterUrlParams)[]

    filterKeys.forEach(key => {
      const previousFilterValue = previouslyAppliedFilters[key]
      const currentFilterValue = currentFilters[key]

      // Handle array-type filters (e.g., amenities, themes) by finding newly added values
      if (Array.isArray(currentFilterValue) && currentFilterValue.length > 0) {
        const previousValuesArray = toArray(previousFilterValue)

        // Find values that exist in current but not in previous, excluding falsy values
        const newlyAddedValues = currentFilterValue.filter(
          value => Boolean(value) && !previousValuesArray.includes(value)
        )

        if (newlyAddedValues.length > 0) {
          newlyAddedValues.forEach(value =>
            newlyAppliedFiltersSet.add(`${key}:${value}`)
          )
        }
      } else if (
        !Array.isArray(currentFilterValue) &&
        currentFilterValue &&
        currentFilterValue !== previousFilterValue &&
        key !== 'priceMin' &&
        key !== 'priceMax'
      ) {
        // Handle single-value filters that have changed
        newlyAppliedFiltersSet.add(`${key}:${currentFilterValue}`)
      }
    })

    // Price filters: Always add if present (getCleanedFilters will handle duplicates)
    if (currentFilters.priceMin || currentFilters.priceMax) {
      newlyAppliedFiltersSet.add(
        `price:${currentFilters.priceMin}-${currentFilters.priceMax}`
      )
    }

    return [...newlyAppliedFiltersSet]
  }

  /**
   * Retrieves the raw list of all tracked filters from localStorage.
   *
   * This is a low-level method that returns filters exactly as they are stored,
   * without any validation, filtering, or transformation. Useful for debugging
   * or when you need access to the complete unfiltered list.
   *
   * ## Returns:
   * Array of filter strings in the format "filterType:filterValue"
   *
   * ## Example:
   * ```typescript
   * ['facilities:1', 'facilities:7', 'starRatings:4', 'price:100-200', 'reviewScore:9']
   * ```
   *
   * ## Returns empty array if:
   * - User has never applied any filters (first visit)
   * - localStorage was cleared manually or by browser
   * - Running on server (SSR)
   *
   * @returns Array of filter strings, or empty array if no data exists
   */
  const getAllRecentlyUsedFilters = (): string[] => {
    // Skip if running on server (SSR)
    if (typeof window === 'undefined') return []

    const storedRecentlyUsedData = safelyGetStorageItem(
      localStorage,
      RECENTLY_USED_FILTERS_STORAGE_KEY
    )

    if (storedRecentlyUsedData) {
      return JSON.parse(storedRecentlyUsedData) as string[]
    }

    return []
  }

  /**
   * Retrieves recently used filters that are validated and relevant to the current search context.
   *
   * This is the **primary method** for getting filter suggestions to display to users.
   * It fetches stored filters and validates them against the current search context,
   * ensuring only meaningful suggestions are shown.
   *
   * ## Validation Criteria:
   * Filters are **only returned** if they pass ALL of these checks:
   *
   * 1. **Not already selected** - Filters the user has already applied are excluded
   * 2. **Available in current results** - Filter must exist in facets with count \> 0
   * 3. **Valid for current location** - Neighborhoods/brands must be available
   *
   * ## Examples:
   *
   * ### Scenario 1: User previously used "Pool" filter
   * ```typescript
   * Stored: ['facilities:1']  // facility ID 1 = Pool
   * Current search has Pool available → Returns [{ id: 'facilities:1', type: 'facilities', value: '1' }]
   * Current search doesn't have Pool → Returns []
   * ```
   *
   * ### Scenario 2: User already selected "Pool"
   * ```typescript
   * Stored: ['facilities:1']
   * Applied filters: { facilities: ['1'] }
   * → Returns [] (already selected, don't show again)
   * ```
   *
   * ### Scenario 3: Multiple filters, some available
   * ```typescript
   * Stored: ['facilities:1', 'facilities:7', 'starRatings:5']
   * Facets: { facilities: [{ id: '1', value: 10 }] }  // Only pool available
   * → Returns [{ id: 'facilities:1', ... }]  // Only pool, others unavailable
   * ```
   *
   * ## Return Format:
   * ```typescript
   * [
   *   { id: 'facilities:1', type: 'facilities', value: '1' },
   *   { id: 'price:100-200', type: 'price', value: '100-200' }
   * ]
   * ```
   *
   * @param appliedFilters - Currently active filters from URL (to avoid showing duplicates)
   * @param facets - Available filter facets from search API response
   * @param neighborhoods - Available neighborhoods for current search location
   * @param propertyBrands - Available hotel brands for current search
   * @param limit - Maximum number of suggestions to return
   * @returns Array of validated filter objects ready to display in UI
   */
  const getRecentlyUsedFilters = ({
    appliedFilters,
    facets,
    neighborhoods,
    propertyBrands,
    isCheckInAtLeastTwoDaysAway,
    limit
  }: Omit<GetAvailableRecentlyUsedFiltersProps, 'storedFilters'>) => {
    // Skip if running on server (SSR)
    if (typeof window === 'undefined') return []

    // Get raw stored filters
    const storedFilters = getAllRecentlyUsedFilters()

    // Validate and filter based on current search context
    const filters = getAvailableRecentlyUsedFilters({
      storedFilters,
      appliedFilters,
      facets,
      neighborhoods,
      propertyBrands,
      isCheckInAtLeastTwoDaysAway,
      limit
    })

    // Respect the limit parameter
    return filters.slice(0, limit)
  }

  const getMappedAllRecentlyUsedFilters = ({
    limit,
    neighborhoods,
    propertyBrands
  }: Pick<
    GetAvailableRecentlyUsedFiltersProps,
    'limit' | 'neighborhoods' | 'propertyBrands'
  >) => {
    const storedFilters = getAllRecentlyUsedFilters()
    return getValidMappedAllRecentlyUsedFilters(
      storedFilters,
      neighborhoods,
      propertyBrands
    ).slice(0, limit)
  }

  return {
    getAllRecentlyUsedFilters,
    getRecentlyUsedFilters,
    getMappedAllRecentlyUsedFilters
  }
}
